import React from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, Dribbble, Twitter, Github, Mail, MapPin, ArrowUpRight } from 'lucide-react';
import { personalInfo, navigationLinks } from '../../data/mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { icon: Linkedin, href: personalInfo.social.linkedin, label: 'LinkedIn' },
    { icon: Dribbble, href: personalInfo.social.dribbble, label: 'Dribbble' },
    { icon: Twitter, href: personalInfo.social.twitter, label: 'Twitter' },
    { icon: Github, href: personalInfo.social.github, label: 'GitHub' },
  ];

  return (
    <footer className="relative border-t border-white/5">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/50" />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 relative z-10">
        {/* Top Section */}
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 mb-16">
          {/* Brand */}
          <div className="lg:col-span-5">
            <Link to="/" className="group inline-flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#00ff88] to-[#00cc6a] flex items-center justify-center transform group-hover:rotate-12 transition-transform duration-300">
                <span className="text-black font-bold text-xl">A</span>
              </div>
              <span className="text-white font-bold text-2xl">{personalInfo.name}</span>
            </Link>
            <p className="text-white/40 text-lg leading-relaxed max-w-md mb-8">
              {personalInfo.tagline}. Creating digital experiences that leave lasting impressions.
            </p>
            <div className="flex items-center gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group w-12 h-12 rounded-full bg-white/5 flex items-center justify-center text-white/40 hover:bg-[#00ff88] hover:text-black transition-all duration-300"
                  aria-label={social.label}
                >
                  <social.icon size={20} />
                </a>
              ))}
            </div>
          </div>

          {/* Navigation */}
          <div className="lg:col-span-3">
            <h4 className="text-white font-semibold mb-6">Navigation</h4>
            <ul className="space-y-4">
              {navigationLinks.slice(0, 5).map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="group flex items-center gap-2 text-white/40 hover:text-[#00ff88] transition-colors"
                  >
                    <span>{link.label}</span>
                    <ArrowUpRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-4">
            <h4 className="text-white font-semibold mb-6">Get in Touch</h4>
            <div className="space-y-4">
              <a
                href={`mailto:${personalInfo.email}`}
                className="flex items-center gap-3 text-white/40 hover:text-[#00ff88] transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center">
                  <Mail size={18} />
                </div>
                {personalInfo.email}
              </a>
              <div className="flex items-center gap-3 text-white/40">
                <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center">
                  <MapPin size={18} />
                </div>
                {personalInfo.location}
              </div>
            </div>

            <div className="mt-8">
              <Link to="/contact">
                <button className="group w-full px-6 py-4 bg-white/5 border border-white/10 text-white rounded-full font-medium hover:bg-[#00ff88] hover:border-[#00ff88] hover:text-black transition-all duration-300">
                  <span className="flex items-center justify-center gap-2">
                    Start a Project
                    <ArrowUpRight size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                  </span>
                </button>
              </Link>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/30 text-sm">
            © {currentYear} {personalInfo.name}. All rights reserved.
          </p>
          <p className="text-white/30 text-sm flex items-center gap-2">
            Crafted with <span className="text-[#00ff88]">♥</span> and lots of coffee
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
